import React, { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import {
  Card, Table, Button, Space, Tag, Modal, Form, Input, InputNumber,
  Select, DatePicker, Switch, Tabs, message, Popconfirm,
  Row, Col, Typography
} from 'antd';
import {
  GiftOutlined, TrophyOutlined, HistoryOutlined, PlusOutlined,
  EditOutlined, DeleteOutlined, EyeOutlined, ReloadOutlined,
  SearchOutlined, StarOutlined,
  MinusCircleOutlined
} from '@ant-design/icons';
import { lotteryAPI, configAPI } from '../services/api';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const STATUS_MAP = {
  0: { text: '未开始', color: 'default' },
  1: { text: '进行中', color: 'green' },
  2: { text: '已结束', color: 'red' }
};

const COST_TYPE_MAP = {
  diamond: { text: '抽奖钻石', icon: '💎', color: 'blue' }
};

const PRIZE_TYPE_MAP = {
  item: { text: '道具', color: 'blue', icon: '📦' },
  currency: { text: '货币', color: 'gold', icon: '💰' },
  special: { text: '特殊', color: 'red', icon: '⭐' }
};

const COST_RECORD_TYPE_MAP = {
  diamond: { text: '抽奖钻石', color: 'blue', icon: '💎' }
};

const FLOW_TYPE_MAP = {
  recharge: { text: '充值到账', color: 'green', icon: '💰' },
  lottery_draw: { text: '抽奖扣费', color: 'blue', icon: '🎰' },
  lottery_refund: { text: '抽奖退款', color: 'orange', icon: '↩' },
  consume: { text: '钱包扣减', color: 'red', icon: '📉' }
};

function LotteryManagement() {
  const [activeTab, setActiveTab] = useState('activities');
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [recordPagination, setRecordPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [activityModalVisible, setActivityModalVisible] = useState(false);
  const [prizeModalVisible, setPrizeModalVisible] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [editingPrize, setEditingPrize] = useState(null);
  const [activityForm] = Form.useForm();
  const [prizeForm] = Form.useForm();
  const [itemList, setItemList] = useState([]);
  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [itemSearchText, setItemSearchText] = useState('');
  const [levelModalVisible, setLevelModalVisible] = useState(false);
  const [currentItemForLevel, setCurrentItemForLevel] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [selectedPrizeItem, setSelectedPrizeItem] = useState(null);
  const { current: activityPageCurrent, pageSize: activityPageSize } = pagination;
  const { current: recordPageCurrent, pageSize: recordPageSize } = recordPagination;

  const getFlowConfig = (record) => {
    return FLOW_TYPE_MAP[record.flow_type] || { text: record.flow_type || '流水', color: 'default', icon: '🧾' };
  };

  const renderChangeAmount = (amount) => {
    const numericAmount = Number(amount || 0);
    const prefix = numericAmount > 0 ? '+' : '';
    const color = numericAmount > 0 ? '#389e0d' : numericAmount < 0 ? '#cf1322' : '#595959';
    return <span style={{ color, fontWeight: 700 }}>{prefix}{numericAmount}</span>;
  };

  const getDeliveryStatusTag = (record) => {
    if (record.flow_type === 'recharge') {
      return <Tag color="green">已到账</Tag>;
    }
    if (record.flow_type === 'lottery_refund') {
      return <Tag color="orange">已退款</Tag>;
    }
    if (record.flow_type === 'lottery_draw') {
      if (record.delivery_status === 'delivered') {
        return <Tag color="green">已发奖</Tag>;
      }
      if (record.delivery_status === 'refunded') {
        return <Tag color="orange">已退款</Tag>;
      }
      if (record.delivery_status === 'refund_failed') {
        return <Tag color="red">退款失败</Tag>;
      }
      if (record.delivery_status === 'pending') {
        return <Tag color="processing">待发放</Tag>;
      }
    }
    return <Tag>{record.delivery_status || '-'}</Tag>;
  };

  const getRecordRewardInfo = (record) => {
    const mapRewardItem = (item) => {
      if (!item) return null;
      const rewardId = item.id ?? item.gid ?? item.item_id ?? null;
      const rewardLevel = Number(item.level ?? 0);
      const rewardCount = Number(item.count ?? item.item_count ?? 0);
      const matchedItem = rewardId !== null
        ? itemList.find((configItem) => String(configItem.id) === String(rewardId))
        : null;

      return {
        id: rewardId,
        level: rewardLevel,
        count: rewardCount,
        name: item.name || matchedItem?.name || (rewardId !== null ? `道具${rewardId}` : '未知道具')
      };
    };

    try {
      const payload = typeof record.item_data === 'string' ? JSON.parse(record.item_data) : (record.item_data || {});
      let rewards = Array.isArray(payload.rewards) ? payload.rewards : [];

      if (rewards.length === 0 && payload.prize_item_data) {
        let fallbackReward = payload.prize_item_data;
        if (typeof fallbackReward === 'string') {
          try {
            fallbackReward = JSON.parse(fallbackReward);
          } catch (error) {
            fallbackReward = null;
          }
        }
        if (Array.isArray(fallbackReward)) {
          rewards = fallbackReward;
        } else if (fallbackReward && typeof fallbackReward === 'object') {
          rewards = [fallbackReward];
        }
      }

      return rewards
        .map(mapRewardItem)
        .filter((item) => item && (item.id !== null || item.name) && item.count > 0);
    } catch (error) {
      return [];
    }
  };

  const loadActivities = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const response = await lotteryAPI.getActivities({
        page: activityPageCurrent,
        limit: activityPageSize,
        ...params
      });
      if (response.data.success) {
        const data = response.data.data;
        const list = Array.isArray(data) ? data : (data.activities || data.list || data.records || []);
        const total = Array.isArray(data) ? data.length : (data.pagination?.total || data.total || 0);
        setActivities(list);
        setPagination(prev => ({
          ...prev,
          total: total
        }));
      }
    } catch (error) {
      console.error('加载活动列表失败:', error);
      message.error('加载活动列表失败');
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [activityPageCurrent, activityPageSize]);

  const loadItemList = useCallback(async () => {
    try {
      const response = await configAPI.getItems();
      if (response.data.success) {
        const items = response.data.data || [];
        const formattedItems = items.map(item => ({
          id: item.item_id || item.id,
          name: item.name || `道具${item.item_id || item.id}`,
          type: item.type || 'resource',
          levels: item.levels || [],
          hasMultipleLevels: item.hasMultipleLevels || false,
          defaultLevel: item.defaultLevel || null,
          quality: item.quality,
          description: item.description
        }));
        setItemList(formattedItems);
      } else {
        setItemList([]);
      }
    } catch (error) {
      setItemList([]);
    }
  }, []);

  const loadPrizes = useCallback(async (activityId) => {
    try {
      const response = await lotteryAPI.getPrizes(activityId);
      if (response.data.success) {
        const data = response.data.data;
        const list = Array.isArray(data) ? data : (data.list || data.records || []);
        setPrizes(list);
      }
    } catch (error) {
      console.error('加载奖品列表失败:', error);
      message.error('加载奖品列表失败');
      setPrizes([]);
    }
  }, []);

  const loadRecords = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const response = await lotteryAPI.getRecords({
        page: recordPageCurrent,
        limit: recordPageSize,
        ...params
      });
      if (response.data.success) {
        const data = response.data.data;
        const list = Array.isArray(data) ? data : (data.list || data.records || []);
        const total = Array.isArray(data) ? data.length : (data.pagination?.total || data.total || 0);
        setRecords(list);
        setRecordPagination(prev => ({
          ...prev,
          total: total
        }));
      }
    } catch (error) {
      console.error('加载抽奖记录失败:', error);
      message.error('加载抽奖记录失败');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [recordPageCurrent, recordPageSize]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  useEffect(() => {
    loadItemList();
  }, [loadItemList]);

  useEffect(() => {
    if (activeTab === 'prizes' && selectedActivity) {
      loadPrizes(selectedActivity);
    }
  }, [activeTab, selectedActivity, loadPrizes]);

  useEffect(() => {
    if (activeTab === 'records') {
      loadRecords();
    }
  }, [activeTab, loadRecords]);

  const handleCreateActivity = () => {
    setEditingActivity(null);
    activityForm.resetFields();
    activityForm.setFieldsValue({
      status: true,
      cost_amount: 100,
      max_draws_per_day: 0,
      max_draws_total: 0,
      sort_order: 0
    });
    setActivityModalVisible(true);
  };

  const handleEditActivity = (record) => {
    setEditingActivity(record);
    activityForm.setFieldsValue({
      ...record,
      cost_amount: Number(record.cost_amount || 0),
      max_draws_per_day: Number(record.max_draws_per_day || 0),
      max_draws_total: Number(record.max_draws_total || 0),
      sort_order: Number(record.sort_order || 0),
      status: Number(record.status) === 1,
      time_range: record.start_time && record.end_time ? [
        dayjs(record.start_time),
        dayjs(record.end_time)
      ] : null
    });
    setActivityModalVisible(true);
  };

  const handleActivitySubmit = async (values) => {
    try {
      const data = {
        ...values,
        cost_type: 'diamond',
        cost_amount: Number(values.cost_amount || 0),
        max_draws_per_day: Number(values.max_draws_per_day || 0),
        max_draws_total: Number(values.max_draws_total || 0),
        sort_order: Number(values.sort_order || 0),
        status: values.status ? 1 : 0
      };
      if (values.time_range) {
        data.start_time = values.time_range[0].toISOString();
        data.end_time = values.time_range[1].toISOString();
      }
      delete data.time_range;

      if (editingActivity) {
        await lotteryAPI.updateActivity(editingActivity.id, data);
        message.success('活动更新成功');
      } else {
        await lotteryAPI.createActivity(data);
      message.success('活动创建成功');
      }
      setActivityModalVisible(false);
      loadActivities();
    } catch (error) {
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  const handleDeleteActivity = async (id) => {
    try {
      await lotteryAPI.deleteActivity(id);
      message.success('活动删除成功');
      loadActivities();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleCreatePrize = () => {
    if (!selectedActivity) {
      message.warning('请先选择一个活动');
      return;
    }
    setEditingPrize(null);
    prizeForm.resetFields();
    prizeForm.setFieldsValue({ activity_id: selectedActivity, type: 'item', probability: 10, item_count: 1 });
    setSelectedPrizeItem(null);
    setItemSearchText('');
    setPrizeModalVisible(true);
  };

  const handleEditPrize = (record) => {
    setEditingPrize(record);
    let parsedItemData = null;
    try {
      parsedItemData = typeof record.item_data === 'string' && record.item_data
        ? JSON.parse(record.item_data)
        : record.item_data;
    } catch (error) {
      parsedItemData = null;
    }
    const itemMeta = record.type === 'item' ? {
      id: parsedItemData?.id || record.item_id || 0,
      name: parsedItemData?.name || record.name || `道具${record.item_id}`,
      level: parsedItemData?.level ?? 0,
      type: parsedItemData?.type || 'item',
      quality: parsedItemData?.quality,
      description: parsedItemData?.description
    } : null;
    setSelectedPrizeItem(itemMeta && itemMeta.id ? itemMeta : null);
    prizeForm.setFieldsValue(record);
    setPrizeModalVisible(true);
  };

  const applySelectedPrizeItem = (item, level) => {
    const normalizedLevel = Number(level ?? item.defaultLevel ?? 0);
    const nextItem = {
      id: item.id,
      name: item.name || `道具${item.id}`,
      level: normalizedLevel,
      type: item.type || 'item',
      quality: item.quality,
      description: item.description
    };
    setSelectedPrizeItem(nextItem);
    prizeForm.setFieldsValue({
      item_id: item.id,
      name: prizeForm.getFieldValue('name') || nextItem.name
    });
    setItemModalVisible(false);
  };

  const handleSelectPrizeItem = (item) => {
    if (item.levels && item.levels.length > 0) {
      setCurrentItemForLevel(item);
      setSelectedLevel(item.levels[0].level);
      setLevelModalVisible(true);
      return;
    }
    applySelectedPrizeItem(item, item.defaultLevel || 1);
  };

  const handleConfirmPrizeLevel = () => {
    if (!currentItemForLevel || selectedLevel === null || selectedLevel === undefined) {
      return;
    }
    applySelectedPrizeItem(currentItemForLevel, selectedLevel);
    setLevelModalVisible(false);
    setCurrentItemForLevel(null);
    setSelectedLevel(null);
  };

  const handleClearPrizeItem = () => {
    setSelectedPrizeItem(null);
    prizeForm.setFieldsValue({ item_id: undefined });
  };

  const handlePrizeSubmit = async (values) => {
    try {
      const data = { ...values };
      if (values.type === 'item') {
        const itemId = selectedPrizeItem?.id || values.item_id;
        if (!itemId) {
          message.error('请选择奖品道具');
          return;
        }
        data.item_id = itemId;
        data.item_data = JSON.stringify({
          type: 'item',
          id: itemId,
          level: Number(selectedPrizeItem?.level ?? 0),
          count: Number(values.item_count || 1),
          name: selectedPrizeItem?.name || values.name || `道具${itemId}`,
          quality: selectedPrizeItem?.quality,
          description: selectedPrizeItem?.description
        });
      } else {
        data.item_data = values.item_data || '';
      }
      if (editingPrize) {
        await lotteryAPI.updatePrize(editingPrize.id, data);
        message.success('奖品更新成功');
      } else {
        await lotteryAPI.createPrize({ ...data, activity_id: selectedActivity });
        message.success('奖品添加成功');
      }
      setPrizeModalVisible(false);
      loadPrizes(selectedActivity);
    } catch (error) {
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  const handleDeletePrize = async (id) => {
    try {
      await lotteryAPI.deletePrize(id);
      message.success('奖品删除成功');
      loadPrizes(selectedActivity);
    } catch (error) {
      message.error('删除失败');
    }
  };

  const activityColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '活动名称', dataIndex: 'name', key: 'name', width: 150, ellipsis: true },
    {
      title: '消耗方式', dataIndex: 'cost_type', key: 'cost_type', width: 140,
      render: (type) => {
        const config = COST_TYPE_MAP[type] || COST_TYPE_MAP.diamond;
        return <Tag color={config.color}>{config.icon} {config.text}</Tag>;
      }
    },
    { title: '消耗数量', dataIndex: 'cost_amount', key: 'cost_amount', width: 100 },
    {
      title: '每日上限', dataIndex: 'max_draws_per_day', key: 'max_draws_per_day', width: 100,
      render: (v) => v || '无限制'
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 80,
      render: (status) => {
        const normalizedStatus = Number(status);
        const config = STATUS_MAP[normalizedStatus] || STATUS_MAP[0];
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '时间', dataIndex: 'start_time', key: 'time', width: 200,
      ellipsis: true,
      render: (_, r) => r.start_time ? `${new Date(r.start_time).toLocaleDateString()} ~ ${r.end_time ? new Date(r.end_time).toLocaleDateString() : '长期'}` : '长期'
    },
    {
      title: '操作', key: 'action', width: 200, fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => { setSelectedActivity(record.id); setActiveTab('prizes'); }}>奖品</Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditActivity(record)}>编辑</Button>
          <Popconfirm title="确定删除此活动？" onConfirm={() => handleDeleteActivity(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const prizeColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '奖品名称', dataIndex: 'name', key: 'name', width: 150 },
    {
      title: '类型', dataIndex: 'type', key: 'type', width: 80,
      render: (type) => {
        const config = PRIZE_TYPE_MAP[type] || PRIZE_TYPE_MAP.item;
        return <Tag color={config.color}>{config.icon} {config.text}</Tag>;
      }
    },
    { title: '概率(%)', dataIndex: 'probability', key: 'probability', width: 100, render: (v) => `${v}%` },
    { title: '数量', dataIndex: 'item_count', key: 'item_count', width: 80 },
    { title: '排序', dataIndex: 'sort_order', key: 'sort_order', width: 80 },
    {
      title: '特殊奖品', dataIndex: 'is_special', key: 'is_special', width: 100,
      render: (v) => v ? <Tag color="red"><StarOutlined /> 特殊</Tag> : <Tag>普通</Tag>
    },
    {
      title: '操作', key: 'action', width: 150,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditPrize(record)}>编辑</Button>
          <Popconfirm title="确定删除此奖品？" onConfirm={() => handleDeletePrize(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const recordColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    {
      title: '流水类型', dataIndex: 'flow_type', key: 'flow_type', width: 110,
      render: (_, record) => {
        const config = getFlowConfig(record);
        return <Tag color={config.color}>{config.icon} {config.text}</Tag>;
      }
    },
    { title: '活动名称', dataIndex: 'activity_name', key: 'activity_name', width: 130, ellipsis: true, render: (value) => value || '-' },
    { title: '角色', dataIndex: 'user_name', key: 'user_name', width: 120, ellipsis: true, render: (value, record) => value || `角色ID:${record.user_id || '-'}` },
    {
      title: '来源单号',
      dataIndex: 'source_no',
      key: 'source_no',
      width: 180,
      ellipsis: true,
      render: (value, record) => value || record.draw_no || '-'
    },
    {
      title: '奖品/类型', key: 'prize_type', width: 180,
      render: (_, record) => {
        if (!record.prize_name) {
          const flowConfig = getFlowConfig(record);
          return <Text type="secondary">{flowConfig.text}</Text>;
        }
        const config = PRIZE_TYPE_MAP[record.prize_type] || PRIZE_TYPE_MAP.item;
        return (
          <Space direction="vertical" size={2}>
            <Text strong ellipsis={{ tooltip: record.prize_name }}>{record.prize_name}</Text>
            <Tag color={config.color}>{config.icon} {config.text}</Tag>
          </Space>
        );
      }
    },
    {
      title: '变动',
      key: 'change_amount',
      width: 130,
      render: (_, record) => {
        const config = COST_RECORD_TYPE_MAP.diamond;
        return <Tag color={Number(record.change_amount || 0) >= 0 ? 'green' : config.color}>{config.icon} {renderChangeAmount(record.change_amount)} {config.text}</Tag>;
      }
    },
    {
      title: '余额变化',
      key: 'balance_range',
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Text>前: {Number(record.balance_before || 0)}</Text>
          <Text>后: {Number(record.balance_after || 0)}</Text>
        </Space>
      )
    },
    {
      title: '发放内容',
      key: 'reward_items',
      width: 240,
      render: (_, record) => {
        const rewardItems = getRecordRewardInfo(record);
        if (rewardItems.length === 0) {
          return <Text type="secondary">-</Text>;
        }
        return (
          <Space direction="vertical" size={4}>
            {rewardItems.slice(0, 2).map((item, index) => (
              <Tag key={`${record.id}_${item.id ?? item.name}_${index}`} color="blue">
                {item.name}{item.level ? ` Lv.${item.level}` : ''} x{item.count}
              </Tag>
            ))}
            {rewardItems.length > 2 && <Text type="secondary">共 {rewardItems.length} 项奖励</Text>}
          </Space>
        );
      }
    },
    {
      title: '状态',
      key: 'delivery_status',
      width: 110,
      render: (_, record) => getDeliveryStatusTag(record)
    },
    {
      title: '备注',
      key: 'remark',
      width: 220,
      ellipsis: true,
      render: (_, record) => record.delivery_message || record.remark || '-'
    },
    {
      title: '时间', dataIndex: 'created_at', key: 'created_at', width: 168,
      ellipsis: true,
      render: (t) => t ? new Date(t).toLocaleString('zh-CN') : '-'
    }
  ];

  return (
    <div>
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <Tabs.TabPane tab={<span><GiftOutlined /> 活动管理</span>} key="activities">
          <Card
            title="抽奖活动管理"
            extra={
              <Space>
                <Button icon={<ReloadOutlined />} onClick={() => loadActivities()}>刷新</Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateActivity}>新建活动</Button>
              </Space>
            }
          >
            <Table
              columns={activityColumns}
              dataSource={activities}
              loading={loading}
              pagination={{ ...pagination, onChange: (p) => setPagination(prev => ({ ...prev, current: p })) }}
              rowKey="id"
              tableLayout="fixed"
              scroll={{ x: 1000 }}
            />
          </Card>
        </Tabs.TabPane>

        <Tabs.TabPane tab={<span><TrophyOutlined /> 奖品管理</span>} key="prizes">
          <Card
            title={selectedActivity ? `奖品管理 - 活动ID: ${selectedActivity}` : '奖品管理'}
            extra={
              <Space>
                <Select
                  placeholder="选择活动"
                  style={{ width: 200 }}
                  value={selectedActivity}
                  onChange={(v) => { setSelectedActivity(v); loadPrizes(v); }}
                  options={activities.map(a => ({ label: a.name, value: a.id }))}
                />
                <Button icon={<ReloadOutlined />} onClick={() => selectedActivity && loadPrizes(selectedActivity)}>刷新</Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreatePrize}>添加奖品</Button>
              </Space>
            }
          >
            <Table
              columns={prizeColumns}
              dataSource={prizes}
              loading={loading}
              rowKey="id"
              pagination={false}
            />
          </Card>
        </Tabs.TabPane>

        <Tabs.TabPane tab={<span><HistoryOutlined /> 抽奖记录</span>} key="records">
          <Card
            title="抽奖记录与钱包流水"
            extra={
              <Space>
                <Input.Search placeholder="搜索角色/账号/单号" onSearch={(v) => loadRecords({ keyword: v })} style={{ width: 220 }} />
                <Button icon={<ReloadOutlined />} onClick={() => loadRecords()}>刷新</Button>
              </Space>
            }
          >
            <Table
              columns={recordColumns}
              dataSource={records}
              loading={loading}
              pagination={{ ...recordPagination, onChange: (p) => setRecordPagination(prev => ({ ...prev, current: p })) }}
              rowKey="id"
              tableLayout="fixed"
              scroll={{ x: 1700 }}
            />
          </Card>
        </Tabs.TabPane>
      </Tabs>

      <Modal
        title={editingActivity ? '编辑活动' : '新建活动'}
        open={activityModalVisible}
        onCancel={() => setActivityModalVisible(false)}
        onOk={() => activityForm.submit()}
        width={600}
      >
        <Form form={activityForm} layout="vertical" onFinish={handleActivitySubmit}>
          <Form.Item name="name" label="活动名称" rules={[{ required: true }]}>
            <Input placeholder="请输入活动名称" />
          </Form.Item>
          <Form.Item name="description" label="活动描述">
            <TextArea rows={3} placeholder="请输入活动描述" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="cost_amount" label="消耗数量" rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: '100%' }} addonAfter="抽奖钻石" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <Text type="secondary">消耗固定为后台抽奖钻石钱包，不再支持切换成金币或游戏内道具扣费。</Text>
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="max_draws_per_day" label="每日抽奖上限">
                <InputNumber min={0} placeholder="0表示无限制" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="max_draws_total" label="总抽奖上限">
                <InputNumber min={0} placeholder="0表示无限制" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="time_range" label="活动时间">
            <RangePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="status" label="状态" valuePropName="checked">
            <Switch checkedChildren="上架" unCheckedChildren="下架" />
          </Form.Item>
          <Form.Item name="sort_order" label="排序">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="rules" label="活动规则">
            <TextArea rows={3} placeholder="请输入活动规则" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingPrize ? '编辑奖品' : '添加奖品'}
        open={prizeModalVisible}
        onCancel={() => setPrizeModalVisible(false)}
        onOk={() => prizeForm.submit()}
        width={600}
      >
        <Form form={prizeForm} layout="vertical" onFinish={handlePrizeSubmit}>
          <Form.Item name="name" label="奖品名称" rules={[{ required: true }]}>
            <Input placeholder="请输入奖品名称" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="type" label="奖品类型" rules={[{ required: true }]}>
                <Select>
                  <Option value="item">📦 道具</Option>
                  <Option value="currency">💰 货币</Option>
                  <Option value="special">⭐ 特殊</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="probability" label="概率(%)" rules={[{ required: true }]}>
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item noStyle shouldUpdate={(prev, curr) => prev.type !== curr.type}>
                {({ getFieldValue }) => getFieldValue('type') === 'item' ? (
                  <Form.Item name="item_id" label="道具ID" rules={[{ required: true, message: '请选择道具' }]}>
                    <Input
                      readOnly
                      placeholder="请从列表中选择道具"
                      addonAfter={<Button type="link" size="small" icon={<SearchOutlined />} onClick={() => setItemModalVisible(true)}>选择</Button>}
                    />
                  </Form.Item>
                ) : (
                  <Form.Item name="item_id" label="道具ID">
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="item_count" label="数量">
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.type !== curr.type || prev.item_id !== curr.item_id}>
            {({ getFieldValue }) => getFieldValue('type') === 'item' && (
              <Form.Item label="已选道具">
                {selectedPrizeItem ? (
                  <div style={{
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    padding: '12px',
                    backgroundColor: '#fafafa'
                  }}>
                    <Row gutter={8} align="middle">
                      <Col flex="auto">
                        <Tag color="blue">
                          {selectedPrizeItem.name}{selectedPrizeItem.level ? ` Lv.${selectedPrizeItem.level}` : ''}
                        </Tag>
                        <span style={{ marginLeft: 8 }}>ID: {selectedPrizeItem.id}</span>
                      </Col>
                      <Col>
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<MinusCircleOutlined />}
                          onClick={handleClearPrizeItem}
                        >
                          移除
                        </Button>
                      </Col>
                    </Row>
                  </div>
                ) : (
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    暂未选择道具，点击上方“选择”从列表挑选
                  </Text>
                )}
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item name="sort_order" label="排序">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="image_url" label="奖品图片URL">
            <Input placeholder="请输入图片URL" />
          </Form.Item>
          <Form.Item name="is_special" label="特殊奖品" valuePropName="checked">
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={null}
        open={itemModalVisible}
        onCancel={() => setItemModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setItemModalVisible(false)}>关闭</Button>
        ]}
        width={1120}
        className="mail-item-modal"
      >
        <div className="mail-item-picker">
          <div className="mail-modal-hero mail-modal-hero-compact">
            <div>
              <div className="mail-modal-title">奖品道具选择</div>
              <div className="mail-modal-subtitle">左侧浏览抽奖道具库，右侧确认当前奖品配置，保持和邮件、福利、商城一致的挑选方式。</div>
            </div>
            <div className="mail-modal-hero-tags">
              <Tag color="blue">可选 {
                itemList.filter((item) => {
                  const searchText = itemSearchText.toLowerCase().trim();
                  if (!searchText) return true;
                  return String(item.id).includes(searchText) || (item.name || '').toLowerCase().includes(searchText);
                }).length
              } 个</Tag>
              <Tag color={selectedPrizeItem ? 'green' : 'default'}>{selectedPrizeItem ? '已选 1 个' : '未选择'}</Tag>
            </div>
          </div>

          <div className="mail-item-picker-toolbar">
            <Input
              placeholder="搜索道具：输入 ID 或名称"
              allowClear
              value={itemSearchText}
              onChange={(e) => setItemSearchText(e.target.value)}
              prefix={<SearchOutlined />}
              className="mail-item-search"
            />
          </div>

          <div className="mail-item-picker-body">
            <div className="mail-item-library">
              <div className="mail-item-grid">
                {itemList.filter((item) => {
                  const searchText = itemSearchText.toLowerCase().trim();
                  if (!searchText) return true;
                  return String(item.id).includes(searchText) || (item.name || '').toLowerCase().includes(searchText);
                }).map((record) => {
                  const isSelected = selectedPrizeItem && String(selectedPrizeItem.id) === String(record.id);
                  return (
                    <div key={record.id} className={`mail-item-card${isSelected ? ' mail-item-card-added' : ''}`}>
                      <div className="mail-item-card-head">
                        <div>
                          <div className="mail-item-card-title">{record.name || '未知道具'}</div>
                          <div className="mail-item-card-subtitle">ID {record.id}</div>
                        </div>
                        <Tag color="blue">{record.levels && record.levels.length > 0 ? `${record.levels.length}级` : '单级'}</Tag>
                      </div>
                      <div className="mail-item-card-meta">
                        <span>{record.type || 'item'}</span>
                        <span>{record.quality || 0}星</span>
                      </div>
                      <div className="mail-item-card-desc">{record.description || '暂无描述'}</div>
                      <Button
                        type={isSelected ? 'default' : 'primary'}
                        onClick={() => handleSelectPrizeItem(record)}
                        block
                      >
                        {isSelected ? '已作为当前奖品' : '设为奖品'}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mail-item-sidebar">
              <div className="mail-form-section mail-form-section-tight">
                <div className="mail-form-section-head">
                  <div className="mail-form-section-title">当前奖品</div>
                  <div className="mail-form-section-subtitle">点击左侧道具后，这里会同步显示当前用于抽奖的奖品配置。</div>
                </div>
                {selectedPrizeItem ? (
                  <div className="mail-selected-list">
                    <div className="mail-selected-item">
                      <div className="mail-selected-item-main">
                        <div className="mail-selected-item-title">{selectedPrizeItem.name || `道具${selectedPrizeItem.id}`}{selectedPrizeItem.level ? ` Lv.${selectedPrizeItem.level}` : ''}</div>
                        <div className="mail-selected-item-meta">道具 ID {selectedPrizeItem.id}</div>
                      </div>
                      <div className="mail-selected-item-controls">
                        <Tag color="blue">奖品道具</Tag>
                        <Button type="text" danger size="small" icon={<MinusCircleOutlined />} onClick={handleClearPrizeItem}>
                          清除
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mail-empty-panel">
                    <div className="mail-empty-panel-title">当前没有奖品道具</div>
                    <div className="mail-empty-panel-text">从左侧物品库选择道具后，这里会显示当前奖品。</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        title={`选择等级 - ${currentItemForLevel?.name || ''}`}
        open={levelModalVisible}
        onCancel={() => {
          setLevelModalVisible(false);
          setCurrentItemForLevel(null);
          setSelectedLevel(null);
        }}
        onOk={handleConfirmPrizeLevel}
        width={440}
        okText="确认选择"
        cancelText="取消"
      >
        <div style={{ maxHeight: '300px', overflow: 'auto' }}>
          <Table
            columns={[
              { title: '等级', dataIndex: 'level', key: 'level', width: 70, render: (level) => <Tag color="blue">Lv.{level}</Tag> },
              { title: '品质', dataIndex: 'quality', key: 'quality', width: 70, render: (quality) => <Tag>{quality || '-'}</Tag> },
              { title: '描述', dataIndex: 'description', key: 'description', width: 150, ellipsis: true, render: (text) => <Text type="secondary" ellipsis={{ tooltip: text || '-' }}>{text || '-'}</Text> },
              {
                title: '操作', key: 'action', width: 88,
                render: (_, record) => (
                  <Button size="small" type={selectedLevel === record.level ? 'primary' : 'default'} onClick={() => setSelectedLevel(record.level)}>
                    {selectedLevel === record.level ? '已选' : '选择'}
                  </Button>
                )
              }
            ]}
            dataSource={currentItemForLevel?.levels || []}
            rowKey="level"
            pagination={false}
            size="small"
            tableLayout="fixed"
            scroll={{ x: 378 }}
          />
        </div>
      </Modal>
    </div>
  );
}

export default LotteryManagement;
